import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBooleanToggleField,
  AdminBlogShell,
  AdminEventEditorFields,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  createYowanAdminClass,
  createYowanAdminSeminar,
  createYowanAdminSeminarSeries,
  fetchYowanAdminFacilityList,
  fetchYowanAdminInstructorList,
  fetchYowanAdminOrganizerList,
  fetchYowanAdminRoomList,
  fetchYowanAdminSeminarDetail,
  fetchYowanAdminSeminarSeriesDetail,
  getYowanAdminMutationErrorMessage,
  getYowanApiFieldErrorMessage,
  validateYowanImageUploadFile,
  YOWAN_IMAGE_UPLOAD_HELP_TEXT,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const ADMIN_EVENT_OPTION_PAGE_SIZE = 200;

const buildOption = (value, label, option = {}) => ({
  value: value || '',
  label: label || '未設定',
  ...option,
  disabled: option.disabled === true,
});

const isInactiveMaster = (row = {}) => row.isActive === false || row.is_active === false;

const buildMasterOption = (row = {}, label, { retained = false, ...option } = {}) => {
  const isInactiveRetained = retained && isInactiveMaster(row);

  return buildOption(
    row.id,
    `${label || row.id || '未設定'}${isInactiveRetained ? '（停止中・現在値）' : ''}`,
    { ...option, disabled: isInactiveRetained },
  );
};

const buildOrganizerOptions = (rows = []) => [
  buildOption('', '主催者を選択'),
  ...rows.map((row) => buildMasterOption(row, row.organizerName || row.organizer_name || row.representativeName || row.representative_name || row.id, row)),
];

const buildInstructorOptions = (rows = []) => [
  buildOption('', '講師を選択'),
  ...rows.map((row) => buildMasterOption(row, row.instructorName || row.instructor_name || row.memberName || row.member_name || row.name || row.id, row)),
];

const buildFacilityOptions = (rows = []) => [
  buildOption('', '未選択'),
  ...rows.map((row) => buildMasterOption(row, row.facilityName || row.facility_name || row.shortName || row.short_name || row.id, row)),
];

const buildRoomOptions = (rows = []) => [
  buildOption('', '未選択'),
  ...rows.map((row) => buildMasterOption(
    row,
    [row.facilityName || row.facility_name, row.roomName || row.room_name].filter(Boolean).join(' / ') || row.id,
    { ...row, facilityId: row.facilityId || row.facility_id || row.facility?.id || '' },
  )),
];

const getRoomOptionsForFacility = (rooms = [], facilityId = '', selectedRoomId = '') => {
  const selectedFacilityId = facilityId || '';
  const currentRoomId = selectedRoomId || '';
  const placeholder = rooms.find((option) => !option.value) || { value: '', label: '未選択' };

  if (!selectedFacilityId) {
    return [{ ...placeholder, label: '先に施設を選択' }];
  }

  const matchingRooms = rooms.filter((option) => (
    option.value && String(option.facilityId || '') === String(selectedFacilityId)
  ));
  const currentRoom = currentRoomId
    ? rooms.find((option) => String(option.value || '') === String(currentRoomId))
    : null;
  const shouldAppendCurrentRoom = currentRoom && !matchingRooms.some((option) => (
    String(option.value || '') === String(currentRoomId)
  ));
  const fallbackCurrentRoom = currentRoomId && !currentRoom
    ? { value: currentRoomId, label: '現在設定されている部屋（候補外・現在値）' }
    : null;

  return [
    placeholder,
    ...matchingRooms,
    ...(shouldAppendCurrentRoom ? [currentRoom] : []),
    ...(fallbackCurrentRoom ? [fallbackCurrentRoom] : []),
  ];
};

const buildInitialMasterOptions = (baseOptions = {}) => ({
  ...baseOptions,
  organizers: buildOrganizerOptions([]),
  instructors: buildInstructorOptions([]),
  facilities: buildFacilityOptions([]),
  rooms: buildRoomOptions([]),
});

const normalizeSeminarSeriesUiText = (value = '') => String(value || '')
  .replaceAll('一連の講習会', 'シリーズ')
  .replaceAll('単独講習会', '単独セミナー')
  .replaceAll('Seminars', 'セミナー')
  .replaceAll('Seminar', 'セミナー')
  .replaceAll('Series', 'シリーズ');

const appendOptionIfMissing = (options = [], option = {}) => {
  if (!option.value || options.some((item) => item.value === option.value)) {
    return options;
  }

  return [...options, option];
};

const mergeRowsById = (...groups) => {
  const rowsById = new Map();

  groups.flat().forEach((row) => {
    if (row?.id && !rowsById.has(row.id)) {
      rowsById.set(row.id, row);
    }
  });

  return Array.from(rowsById.values());
};

const getReferencedOrganizerRows = (record = {}) => [
  record.organizer ? { ...record.organizer, retained: true } : null,
  record.series?.organizer ? { ...record.series.organizer, retained: true } : null,
  ...(record.events || []).flatMap((event) => getReferencedOrganizerRows(event)),
].filter((row) => row?.id);

const getReferencedInstructorRows = (record = {}) => [
  ...(record.schedules || []).map((schedule) => schedule.instructor ? { ...schedule.instructor, retained: true } : null),
  ...(record.slots || []).map((slot) => {
    const instructor = slot.instructorProfile || slot.instructor;
    return instructor ? { ...instructor, retained: true } : null;
  }),
  ...(record.events || []).flatMap((event) => getReferencedInstructorRows(event)),
].filter((row) => row?.id);

const getReferencedFacilityRows = (record = {}) => [
  ...(record.schedules || []).map((schedule) => schedule.facility ? { ...schedule.facility, retained: true } : null),
  ...(record.slots || []).map((slot) => slot.facility ? { ...slot.facility, retained: true } : null),
  ...(record.events || []).flatMap((event) => getReferencedFacilityRows(event)),
].filter((row) => row?.id);

const getReferencedRoomRows = (record = {}) => [
  ...(record.schedules || []).map((schedule) => schedule.room ? { ...schedule.room, retained: true } : null),
  ...(record.slots || []).map((slot) => slot.room ? { ...slot.room, retained: true } : null),
  ...(record.events || []).flatMap((event) => getReferencedRoomRows(event)),
].filter((row) => row?.id);

const optionRowsToMasterRows = (options = [], labelKey) => (
  options
    .filter((option) => option?.value)
    .map((option) => ({
      id: option.value,
      [labelKey]: String(option.label || '').replace('（停止中・現在値）', ''),
      facilityId: option.facilityId || '',
      isActive: option.disabled ? false : undefined,
      retained: option.disabled === true,
    }))
);

const getActiveMasterRows = (rows = []) => rows.filter((row) => row?.id && row.retained !== true && row.isActive !== false);

const markMissingReferencedRowsInactive = (referencedRows = [], activeRows = []) => {
  const activeIds = new Set(activeRows.map((row) => row?.id).filter(Boolean));

  return referencedRows.map((row) => {
    const isMissingFromActiveList = row?.id && !activeIds.has(row.id);

    return {
      ...row,
      retained: true,
      isActive: isInactiveMaster(row) || isMissingFromActiveList ? false : row.isActive,
    };
  });
};

const mergeReferencedRows = (existingRows = [], referencedRows = []) => {
  const activeRows = getActiveMasterRows(existingRows);
  return mergeRowsById(existingRows, markMissingReferencedRowsInactive(referencedRows, activeRows));
};

const mergeReferencedMasterOptions = (previousOptions = {}, record = {}) => ({
  ...previousOptions,
  organizers: buildOrganizerOptions(mergeReferencedRows(optionRowsToMasterRows(previousOptions.organizers, 'organizerName'), getReferencedOrganizerRows(record))),
  instructors: buildInstructorOptions(mergeReferencedRows(optionRowsToMasterRows(previousOptions.instructors, 'instructorName'), getReferencedInstructorRows(record))),
  facilities: buildFacilityOptions(mergeReferencedRows(optionRowsToMasterRows(previousOptions.facilities, 'facilityName'), getReferencedFacilityRows(record))),
  rooms: buildRoomOptions(mergeReferencedRows(optionRowsToMasterRows(previousOptions.rooms, 'roomName'), getReferencedRoomRows(record))),
});

const SeminarSeriesFields = ({ content, register, watch, errors, readOnly = false }) => {
  const labels = content?.form?.labels || {};
  const options = content?.form?.options || {};
  const seriesKind = watch?.('seriesKind') || 'standalone';
  const isGrouped = seriesKind === 'grouped';
  const seriesKindLabel = normalizeSeminarSeriesUiText(options.seriesKind?.find((option) => option.value === seriesKind)?.label || seriesKind || '-');
  const seriesSummaryMaxLength = content.form.seriesSummaryMaxLength || undefined;

  if (!labels.seriesKind) {
    return null;
  }

  return (
    <section className="yowan-admin-event-series-fields">
      <header className="yowan-admin-event-series-fields__header">
        <h2 className="yowan-admin-event-series-fields__title">セミナーの構成</h2>
        <p className="yowan-admin-event-series-fields__lead">
          公開面で単独セミナーとして扱うか、シリーズとして複数セミナーをまとめるかを選びます。
        </p>
      </header>

      <div className="c-form__field">
        <span className="c-form__label">{labels.seriesKind}</span>
        {readOnly ? (
          <>
            <input type="hidden" {...register('seriesKind')} />
            <span className="yowan-admin-event-series-fields__readonly">{seriesKindLabel}</span>
          </>
        ) : (
          <div className="c-form__choices">
            {(options.seriesKind || []).map((option) => (
              <label key={option.value} className="c-form__choice">
                <input type="radio" value={option.value} {...register('seriesKind')} />
                <span>{normalizeSeminarSeriesUiText(option.label)}</span>
              </label>
            ))}
          </div>
        )}
        {content.form.seriesKindHelpText ? <p className="yowan-admin-form__field-note">{content.form.seriesKindHelpText}</p> : null}
      </div>

      {!readOnly && labels.publicSlug ? (
        <label className="c-form__field">
          <span className="c-form__label">{labels.publicSlug}</span>
          <input className="c-form__control" type="text" {...register('publicSlug')} />
          {content.form.publicSlugHelpText ? <p className="yowan-admin-form__field-note">{content.form.publicSlugHelpText}</p> : null}
          {errors?.publicSlug ? <em className="yowan-admin-form__error">{errors.publicSlug.message}</em> : null}
        </label>
      ) : null}

      {isGrouped ? (
        <div className="yowan-admin-event-series-fields__grid">
          <label className="c-form__field">
            <span className="c-form__label">{labels.seriesTitle}</span>
            <input
              className="c-form__control"
              type="text"
              readOnly={readOnly}
              {...register('seriesTitle', {
                validate: (value) => !isGrouped || Boolean(String(value || '').trim()) || content.form.errors.required,
              })}
            />
            {content.form.seriesTitleHelpText ? <p className="yowan-admin-form__field-note">{content.form.seriesTitleHelpText}</p> : null}
            {errors?.seriesTitle ? <em className="yowan-admin-form__error">{errors.seriesTitle.message}</em> : null}
          </label>

          <label className="c-form__field">
            <span className="c-form__label">{labels.seriesSubtitle}</span>
            <input className="c-form__control" type="text" readOnly={readOnly} {...register('seriesSubtitle')} />
            {content.form.seriesSubtitleHelpText ? <p className="yowan-admin-form__field-note">{content.form.seriesSubtitleHelpText}</p> : null}
          </label>

          <label className="c-form__field">
            <span className="c-form__label">{labels.seriesSummary}</span>
            <textarea
              className="c-form__control c-form__control--textarea"
              rows="3"
              readOnly={readOnly}
              maxLength={seriesSummaryMaxLength}
              {...register('seriesSummary', seriesSummaryMaxLength ? {
                maxLength: {
                  value: seriesSummaryMaxLength,
                  message: `${seriesSummaryMaxLength}文字以内で入力してください。`,
                },
              } : {})}
            />
            {content.form.seriesSummaryHelpText ? <p className="yowan-admin-form__field-note">{content.form.seriesSummaryHelpText}</p> : null}
            {errors?.seriesSummary ? <em className="yowan-admin-form__error">{errors.seriesSummary.message}</em> : null}
          </label>

          <div className="c-form__field">
            <span className="c-form__label">{labels.seriesOrganizerId}</span>
            <select className="c-form__control" disabled={readOnly} {...register('seriesOrganizerId')}>
              {(options.organizers || []).map((option) => (
                <option key={option.value || 'empty'} value={option.value} disabled={option.disabled === true}>
                  {option.label}
                </option>
              ))}
            </select>
            {content.form.seriesOrganizerHelpText ? <p className="yowan-admin-form__field-note">{content.form.seriesOrganizerHelpText}</p> : null}
          </div>

          <label className="c-form__field">
            <span className="c-form__label">{labels.seriesFullParticipationFee}</span>
            <input className="c-form__control" type="number" min="0" step="100" readOnly={readOnly} {...register('seriesFullParticipationFee')} />
            {content.form.seriesFullParticipationFeeHelpText ? <p className="yowan-admin-form__field-note">{content.form.seriesFullParticipationFeeHelpText}</p> : null}
          </label>

          <div className="c-form__field">
            <span className="c-form__label">{labels.seriesImage}</span>
            <input type="hidden" {...register('series_primary_image_id')} />
            <input
              className="c-form__control"
              type={content.form.imageInputType === 'file' ? 'file' : 'url'}
              accept={content.form.imageInputType === 'file' ? 'image/*' : undefined}
              disabled={readOnly}
              {...register(
                'seriesImage',
                content.form.imageInputType === 'file'
                  ? { validate: validateYowanImageUploadFile }
                  : {},
              )}
            />
            {errors?.seriesImage ? <em className="yowan-admin-form__error">{errors.seriesImage.message}</em> : null}
            {content.form.seriesImageHelpText || YOWAN_IMAGE_UPLOAD_HELP_TEXT ? (
              <p className="yowan-admin-form__field-note">
                {content.form.imageInputType === 'file'
                  ? [content.form.seriesImageHelpText, YOWAN_IMAGE_UPLOAD_HELP_TEXT].filter(Boolean).join(' ')
                  : content.form.seriesImageHelpText}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
};

const groupedSeminarSectionStyle = {
  display: 'grid',
  gap: '1rem',
  padding: '1rem',
  borderRadius: '1rem',
  border: '1px solid rgba(89, 102, 116, 0.15)',
  background: 'rgba(255, 255, 255, 0.72)',
};

const groupedSeminarHeaderStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  alignItems: 'start',
  justifyItems: 'start',
  gap: '0.75rem',
};

const groupedSeminarTitleStyle = {
  margin: 0,
  color: '#24303a',
  fontSize: '1.05rem',
  fontWeight: 700,
};

const groupedSeminarLeadStyle = {
  margin: '0.2rem 0 0',
  color: 'rgba(47, 54, 61, 0.72)',
  fontSize: '0.82rem',
  lineHeight: 1.55,
};

const groupedSeminarButtonStyle = {
  justifySelf: 'start',
  minHeight: '2rem',
  padding: '0.32rem 0.9rem',
  border: 'none',
  borderRadius: '999px',
  background: 'rgba(3, 152, 253, 0.14)',
  color: '#0b5f94',
  fontSize: '0.9rem',
  fontWeight: 700,
  cursor: 'pointer',
};

const groupedSeminarRemoveButtonStyle = {
  ...groupedSeminarButtonStyle,
  justifySelf: 'end',
  background: 'rgba(124, 42, 46, 0.12)',
  color: '#7c2a2e',
};

const groupedSeminarCardStyle = {
  display: 'grid',
  gap: '0.85rem',
  padding: '1rem',
  borderRadius: '0.9rem',
  border: '1px solid rgba(89, 102, 116, 0.16)',
  background: 'rgba(255, 255, 255, 0.78)',
};

const groupedSeminarGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '0.75rem',
};

const groupedSlotSectionStyle = {
  display: 'grid',
  gap: '0.75rem',
  padding: '0.85rem',
  borderRadius: '0.85rem',
  background: 'rgba(89, 102, 116, 0.07)',
};

const createDefaultSeminarSlot = () => ({
  title: '',
  startAt: '',
  endAt: '',
  facilityId: '',
  roomId: '',
  locationDisplayMode: 'facility',
  publicLocationLabel: '',
  publicLocationNote: '',
  privateLocationNote: '',
  thingsToBring: '',
  capacity: '',
  slotFee: '',
  instructorId: '',
  showInstructorProfile: false,
  isOpen: true,
  isActive: true,
});

const normalizeComparableDateTimeValue = (value) => {
  const normalized = String(value || '').trim();

  if (!normalized) {
    return null;
  }

  const timestamp = Date.parse(normalized);

  return Number.isNaN(timestamp) ? null : timestamp;
};

const normalizeComparableTimeValue = (value) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(value || '').trim());

  if (!match) {
    return null;
  }

  return Number(match[1]) * 60 + Number(match[2]);
};

const hasInvalidComparableRange = (startValue, endValue, normalizeValue) => {
  const start = normalizeValue(startValue);
  const end = normalizeValue(endValue);

  return start !== null && end !== null && end < start;
};

const validateFieldRanges = ({
  rows,
  basePath,
  startKey,
  endKey,
  normalizeValue,
  setError,
  clearErrors,
  message,
}) => {
  if (!Array.isArray(rows)) {
    return true;
  }

  let isValid = true;

  rows.forEach((row, index) => {
    const endPath = `${basePath}.${index}.${endKey}`;

    clearErrors?.(endPath);

    if (hasInvalidComparableRange(row?.[startKey], row?.[endKey], normalizeValue)) {
      setError?.(endPath, { type: 'validate', message });
      isValid = false;
    }
  });

  return isValid;
};

export const validateYowanAdminEventDateTimeRanges = ({
  values = {},
  setError,
  clearErrors,
  message = '終了日時は開始日時以降に設定してください。',
}) => {
  const slotsAreValid = validateFieldRanges({
    rows: values.slots,
    basePath: 'slots',
    startKey: 'startAt',
    endKey: 'endAt',
    normalizeValue: normalizeComparableDateTimeValue,
    setError,
    clearErrors,
    message,
  });
  const schedulesAreValid = validateFieldRanges({
    rows: values.schedules,
    basePath: 'schedules',
    startKey: 'startTime',
    endKey: 'endTime',
    normalizeValue: normalizeComparableTimeValue,
    setError,
    clearErrors,
    message,
  });
  const groupedSeminarsAreValid = Array.isArray(values.seminars)
    ? values.seminars.every((seminar, seminarIndex) => validateFieldRanges({
        rows: seminar?.slots,
        basePath: `seminars.${seminarIndex}.slots`,
        startKey: 'startAt',
        endKey: 'endAt',
        normalizeValue: normalizeComparableDateTimeValue,
        setError,
        clearErrors,
        message,
      }))
    : true;

  return slotsAreValid && schedulesAreValid && groupedSeminarsAreValid;
};

const createDefaultGroupedSeminar = () => ({
  title: '',
  subtitle: '',
  organizerId: '',
  isPublished: false,
  acceptsApplications: true,
  isActive: true,
  summary: '',
  fullParticipationFee: '',
  guidance: '',
  image: '',
  slots: [createDefaultSeminarSlot()],
});

const AdminGroupedSeminarFields = ({
  content,
  register,
  errors,
  setValue,
  watch,
  seminarIndexes,
  seminarSlotIndexes,
  onAddSeminar,
  onRemoveSeminar,
  onAddSeminarSlot,
  onRemoveSeminarSlot,
}) => {
  const labels = content.form.labels || {};
  const slotLabels = content.form.slotLabels || {};
  const seminarLabels = content.form.seminarBlockLabels || {};
  const summaryMaxLength = content.form.summaryMaxLength || undefined;
  const guidanceMaxLength = content.form.guidanceMaxLength || undefined;
  const renderFieldNote = (text) => (
    text ? <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">{text}</p> : null
  );

  return (
    <section className="yowan-admin-grouped-seminars" style={groupedSeminarSectionStyle}>
      <div style={groupedSeminarHeaderStyle}>
        <div>
          <h2 style={groupedSeminarTitleStyle}>{normalizeSeminarSeriesUiText(seminarLabels.sectionTitle || 'セミナー')}</h2>
          <p style={groupedSeminarLeadStyle}>{normalizeSeminarSeriesUiText(seminarLabels.sectionLead || 'シリーズに含めるセミナーを追加します。')}</p>
        </div>
        <button type="button" style={groupedSeminarButtonStyle} onClick={onAddSeminar}>
          + {normalizeSeminarSeriesUiText(seminarLabels.addButton || 'セミナーを追加')}
        </button>
      </div>

      {content.form.seminarBlockHelpText ? (
        <p className="yowan-admin-form__field-note" style={{ margin: 0, fontSize: '0.75rem', lineHeight: 1.45 }}>
          {content.form.seminarBlockHelpText}
        </p>
      ) : null}

      {seminarIndexes.map((seminarIndex, seminarPosition) => {
        const slotIndexes = seminarSlotIndexes[seminarIndex] || [0];
        const seminarErrors = errors?.seminars?.[seminarIndex] || {};

        return (
          <article key={seminarIndex} className="yowan-admin-grouped-seminars__card" style={groupedSeminarCardStyle}>
            <div style={groupedSeminarHeaderStyle}>
              <h3 style={groupedSeminarTitleStyle}>{normalizeSeminarSeriesUiText(seminarLabels.itemTitle || 'セミナー')} {seminarPosition + 1}</h3>
              {seminarIndexes.length > 2 ? (
                <button
                  type="button"
                  aria-label={`${normalizeSeminarSeriesUiText(seminarLabels.itemTitle || 'セミナー')} ${seminarPosition + 1} を削除`}
                  style={groupedSeminarRemoveButtonStyle}
                  onClick={() => onRemoveSeminar(seminarIndex)}
                >
                  ×
                </button>
              ) : null}
            </div>

            <div className="yowan-admin-grouped-seminars__grid" style={groupedSeminarGridStyle}>
              <label className="c-form__field">
                <span className="c-form__label">
                  {labels.title}
                  <em className="c-form__required">*</em>
                </span>
                <input
                  className="c-form__control"
                  type="text"
                  {...register(`seminars.${seminarIndex}.title`, { required: content.form.errors.required })}
                />
                {seminarErrors?.title ? <em className="yowan-admin-form__error">{seminarErrors.title.message}</em> : null}
              </label>

              <label className="c-form__field">
                <span className="c-form__label">{labels.subtitle}</span>
                <input className="c-form__control" type="text" {...register(`seminars.${seminarIndex}.subtitle`)} />
                {renderFieldNote(content.form.subtitleHelpText)}
              </label>

              <div className="c-form__field">
                <span className="c-form__label">{labels.organizerId}</span>
                <select className="c-form__control" {...register(`seminars.${seminarIndex}.organizerId`)}>
                  {(content.form.options.organizers || []).map((option) => (
                    <option key={option.value || 'empty'} value={option.value} disabled={option.disabled === true}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {renderFieldNote('未選択の場合は、シリーズの主催者を使います。')}
              </div>

              <AdminNestedBooleanField
                label={labels.isPublished}
                name={`seminars.${seminarIndex}.isPublished`}
                register={register}
                helpText={content.form.isPublishedHelpText}
              />

              <AdminNestedBooleanField
                label={labels.acceptsApplications}
                name={`seminars.${seminarIndex}.acceptsApplications`}
                register={register}
                helpText={content.form.acceptsApplicationsHelpText}
              />

              <label className="c-form__field">
                <span className="c-form__label">{labels.summary}</span>
                <textarea
                  className="c-form__control c-form__control--textarea"
                  rows="3"
                  maxLength={summaryMaxLength}
                  {...register(`seminars.${seminarIndex}.summary`, summaryMaxLength ? {
                    maxLength: {
                      value: summaryMaxLength,
                      message: `${summaryMaxLength}文字以内で入力してください。`,
                    },
                  } : {})}
                />
                {renderFieldNote(content.form.summaryHelpText)}
                {seminarErrors?.summary ? <em className="yowan-admin-form__error">{seminarErrors.summary.message}</em> : null}
              </label>

              <label className="c-form__field">
                <span className="c-form__label">{labels.fullParticipationFee}</span>
                <input className="c-form__control" type="number" min="0" step="100" {...register(`seminars.${seminarIndex}.fullParticipationFee`)} />
                {renderFieldNote(content.form.fullParticipationFeeHelpText)}
              </label>

              <label className="c-form__field">
                <span className="c-form__label">
                  {labels.guidance}
                  <em className="c-form__required">*</em>
                </span>
                <textarea
                  className="c-form__control c-form__control--textarea"
                  rows="6"
                  maxLength={guidanceMaxLength}
                  {...register(`seminars.${seminarIndex}.guidance`, {
                    required: content.form.errors.required,
                    ...(guidanceMaxLength ? {
                      maxLength: {
                        value: guidanceMaxLength,
                        message: `${guidanceMaxLength}文字以内で入力してください。`,
                      },
                    } : {}),
                  })}
                />
                {renderFieldNote(content.form.guidanceHelpText)}
                {seminarErrors?.guidance ? <em className="yowan-admin-form__error">{seminarErrors.guidance.message}</em> : null}
              </label>

              <div className="c-form__field">
                <span className="c-form__label">{labels.image}</span>
                <input type="hidden" {...register(`seminars.${seminarIndex}.primary_image_id`)} />
                <input
                  className="c-form__control"
                  type={content.form.imageInputType === 'file' ? 'file' : 'url'}
                  accept={content.form.imageInputType === 'file' ? 'image/*' : undefined}
                  {...register(`seminars.${seminarIndex}.image`)}
                />
                {renderFieldNote(content.form.imageHelpText)}
              </div>
            </div>

            <section style={groupedSlotSectionStyle}>
              <div style={groupedSeminarHeaderStyle}>
                <div>
                  <h4 style={{ ...groupedSeminarTitleStyle, fontSize: '0.95rem' }}>{slotLabels.sectionTitle}</h4>
                  <p style={groupedSeminarLeadStyle}>{slotLabels.sectionLead}</p>
                </div>
                <button type="button" style={groupedSeminarButtonStyle} onClick={() => onAddSeminarSlot(seminarIndex)}>
                  + {slotLabels.addButton}
                </button>
              </div>

              {slotIndexes.map((slotIndex, slotPosition) => {
                const slotErrors = seminarErrors?.slots?.[slotIndex] || {};
                const selectedFacilityId = watch?.(`seminars.${seminarIndex}.slots.${slotIndex}.facilityId`) || '';
                const selectedRoomId = watch?.(`seminars.${seminarIndex}.slots.${slotIndex}.roomId`) || '';

                return (
                  <div key={slotIndex} className="yowan-admin-grouped-seminars__card yowan-admin-grouped-seminars__card--slot" style={groupedSeminarCardStyle}>
                    <div style={groupedSeminarHeaderStyle}>
                      <h5 style={{ ...groupedSeminarTitleStyle, fontSize: '0.9rem' }}>{slotLabels.itemTitle} {slotPosition + 1}</h5>
                      {slotIndexes.length > 1 ? (
                        <button
                          type="button"
                          aria-label={`${slotLabels.itemTitle} ${slotPosition + 1} を削除`}
                          style={groupedSeminarRemoveButtonStyle}
                          onClick={() => onRemoveSeminarSlot(seminarIndex, slotIndex)}
                        >
                          ×
                        </button>
                      ) : null}
                    </div>

                    <div className="yowan-admin-grouped-seminars__grid yowan-admin-grouped-seminars__grid--slot" style={groupedSeminarGridStyle}>
                      <label className="c-form__field">
                        <span className="c-form__label">{slotLabels.title}</span>
                        <input className="c-form__control" type="text" {...register(`seminars.${seminarIndex}.slots.${slotIndex}.title`)} />
                        {renderFieldNote(content.form.slotTitleHelpText)}
                      </label>

                      <label className="c-form__field">
                        <span className="c-form__label">
                          {slotLabels.startAt}
                          <em className="c-form__required">*</em>
                        </span>
                        <input
                          className="c-form__control"
                          type="datetime-local"
                          {...register(`seminars.${seminarIndex}.slots.${slotIndex}.startAt`, { required: content.form.errors.required })}
                        />
                        {slotErrors?.startAt ? <em className="yowan-admin-form__error">{slotErrors.startAt.message}</em> : null}
                      </label>

                      <label className="c-form__field">
                        <span className="c-form__label">
                          {slotLabels.endAt}
                          <em className="c-form__required">*</em>
                        </span>
                        <input
                          className="c-form__control"
                          type="datetime-local"
                          {...register(`seminars.${seminarIndex}.slots.${slotIndex}.endAt`, { required: content.form.errors.required })}
                        />
                        {slotErrors?.endAt ? <em className="yowan-admin-form__error">{slotErrors.endAt.message}</em> : null}
                      </label>

                      <label className="c-form__field">
                        <span className="c-form__label">{slotLabels.facility}</span>
                        <select
                          className="c-form__control"
                          {...register(`seminars.${seminarIndex}.slots.${slotIndex}.facilityId`, {
                            onChange: () => setValue?.(`seminars.${seminarIndex}.slots.${slotIndex}.roomId`, '', { shouldDirty: true, shouldValidate: true }),
                          })}
                        >
                          {(content.form.options.facilities || []).map((option) => (
                            <option key={option.value} value={option.value} disabled={option.disabled === true}>{option.label}</option>
                          ))}
                        </select>
                        {renderFieldNote(content.form.facilityHelpText)}
                      </label>

                      <label className="c-form__field">
                        <span className="c-form__label">{slotLabels.room}</span>
                        <select
                          className="c-form__control"
                          disabled={!selectedFacilityId}
                          {...register(`seminars.${seminarIndex}.slots.${slotIndex}.roomId`)}
                        >
                          {getRoomOptionsForFacility(content.form.options.rooms || [], selectedFacilityId, selectedRoomId).map((option) => (
                            <option key={option.value} value={option.value} disabled={option.disabled === true}>{option.label}</option>
                          ))}
                        </select>
                        {renderFieldNote(content.form.roomHelpText)}
                      </label>

                      <div className="c-form__field">
                        <span className="c-form__label">{slotLabels.locationDisplayMode}</span>
                        <select className="c-form__control" {...register(`seminars.${seminarIndex}.slots.${slotIndex}.locationDisplayMode`)}>
                          {(content.form.options.locationDisplayModes || []).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        {renderFieldNote(content.form.locationDisplayModeHelpText)}
                      </div>

                      <label className="c-form__field">
                        <span className="c-form__label">{slotLabels.publicLocationLabel}</span>
                        <input className="c-form__control" type="text" {...register(`seminars.${seminarIndex}.slots.${slotIndex}.publicLocationLabel`)} />
                        {renderFieldNote(content.form.publicLocationLabelHelpText)}
                      </label>

                      <label className="c-form__field">
                        <span className="c-form__label">{slotLabels.publicLocationNote}</span>
                        <textarea className="c-form__control c-form__control--textarea" rows="3" {...register(`seminars.${seminarIndex}.slots.${slotIndex}.publicLocationNote`)} />
                        {renderFieldNote(content.form.publicLocationNoteHelpText)}
                      </label>

                      <label className="c-form__field">
                        <span className="c-form__label">{slotLabels.privateLocationNote}</span>
                        <textarea className="c-form__control c-form__control--textarea" rows="3" {...register(`seminars.${seminarIndex}.slots.${slotIndex}.privateLocationNote`)} />
                        {renderFieldNote(content.form.privateLocationNoteHelpText)}
                      </label>

                      <label className="c-form__field">
                        <span className="c-form__label">{slotLabels.thingsToBring}</span>
                        <textarea className="c-form__control c-form__control--textarea" rows="3" {...register(`seminars.${seminarIndex}.slots.${slotIndex}.thingsToBring`)} />
                        {renderFieldNote(content.form.thingsToBringHelpText)}
                      </label>

                      <label className="c-form__field">
                        <span className="c-form__label">
                          {slotLabels.capacity}
                          <em className="c-form__required">*</em>
                        </span>
                        <input
                          className="c-form__control"
                          type="number"
                          min="0"
                          step="1"
                          {...register(`seminars.${seminarIndex}.slots.${slotIndex}.capacity`, { required: content.form.errors.required })}
                        />
                        {slotErrors?.capacity ? <em className="yowan-admin-form__error">{slotErrors.capacity.message}</em> : null}
                      </label>

                      <label className="c-form__field">
                        <span className="c-form__label">
                          {slotLabels.slotFee}
                          <em className="c-form__required">*</em>
                        </span>
                        <input
                          className="c-form__control"
                          type="number"
                          min="0"
                          step="100"
                          {...register(`seminars.${seminarIndex}.slots.${slotIndex}.slotFee`, { required: content.form.errors.required })}
                        />
                        {slotErrors?.slotFee ? <em className="yowan-admin-form__error">{slotErrors.slotFee.message}</em> : null}
                      </label>

                      <label className="c-form__field">
                        <span className="c-form__label">
                          {slotLabels.instructor}
                          <em className="c-form__required">*</em>
                        </span>
                        <select
                          className="c-form__control"
                          {...register(`seminars.${seminarIndex}.slots.${slotIndex}.instructorId`, { required: content.form.errors.required })}
                        >
                          {(content.form.options.instructors || []).map((option) => (
                            <option key={option.value || 'empty'} value={option.value} disabled={option.disabled === true}>{option.label}</option>
                          ))}
                        </select>
                        {slotErrors?.instructorId ? <em className="yowan-admin-form__error">{slotErrors.instructorId.message}</em> : null}
                      </label>

                      <AdminNestedBooleanField
                        label={slotLabels.showInstructorProfile}
                        name={`seminars.${seminarIndex}.slots.${slotIndex}.showInstructorProfile`}
                        register={register}
                        helpText={content.form.slotShowInstructorProfileHelpText}
                      />

                      <AdminNestedBooleanField
                        label={slotLabels.isOpen}
                        name={`seminars.${seminarIndex}.slots.${slotIndex}.isOpen`}
                        register={register}
                      />

                      <AdminNestedBooleanField
                        label={slotLabels.isActive}
                        name={`seminars.${seminarIndex}.slots.${slotIndex}.isActive`}
                        register={register}
                      />
                    </div>
                  </div>
                );
              })}
            </section>
          </article>
        );
      })}
    </section>
  );
};

const AdminNestedBooleanField = ({ label, name, register, helpText }) => (
  label ? (
    <div className="c-form__field yowan-admin-form__toggle-field">
      <span className="c-form__label">{label}</span>
      <label className="c-form__choice yowan-admin-form__toggle-choice">
        <input type="checkbox" {...register(name)} />
        <span>{label}</span>
      </label>
      {helpText ? <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">{helpText}</p> : null}
    </div>
  ) : null
);

const buildOptionsContent = (content, masterOptions) => ({
  ...content,
  form: {
    ...content?.form,
    options: {
      ...content?.form?.options,
      ...masterOptions,
    },
  },
});

const seminarSlotToDuplicateDefaults = (slot = {}) => ({
  id: '',
  title: slot.title || '',
  startAt: '',
  endAt: '',
  facilityId: slot.facilityId || slot.facility?.id || slot.room?.facility?.id || '',
  roomId: slot.roomId || slot.room?.id || '',
  locationDisplayMode: slot.locationDisplayMode || 'facility',
  publicLocationLabel: slot.publicLocationLabel || '',
  publicLocationNote: slot.publicLocationNote || '',
  privateLocationNote: slot.privateLocationNote || '',
  thingsToBring: slot.thingsToBring || slot.things_to_bring || '',
  capacity: slot.capacity ?? '',
  slotFee: slot.slotFee ?? '',
  instructorId: slot.instructorProfileId || slot.instructorId || '',
  showInstructorProfile: slot.showInstructorProfile === true,
  isOpen: false,
  isActive: true,
});

const seminarToGroupedDuplicateDefaults = (record = {}) => ({
  title: record.title || '',
  subtitle: record.subtitle || '',
  organizerId: record.organizerId || '',
  isPublished: false,
  acceptsApplications: false,
  isActive: true,
  summary: record.summary || '',
  fullParticipationFee: record.seminarFullParticipationFee ?? '',
  guidance: record.guidance || '',
  primary_image_id: record.primary_image_id || record.primary_image?.id || '',
  image: '',
  slots: (record.slots || []).length
    ? record.slots.map(seminarSlotToDuplicateDefaults)
    : [createDefaultSeminarSlot()],
});

const seminarToDuplicateDefaults = (record, fallbackDefaults = {}) => ({
  ...fallbackDefaults,
  title: `${record.title || ''} copy`,
  subtitle: record.subtitle || '',
  seriesKind: 'standalone',
  seriesTitle: '',
  seriesSubtitle: '',
  seriesSummary: '',
  seriesOrganizerId: '',
  seriesFullParticipationFee: '',
  series_primary_image_id: '',
  seriesImage: '',
  eventType: 'spot',
  organizerId: record.organizerId || '',
  isPublished: false,
  acceptsApplications: false,
  isActive: true,
  summary: record.summary || '',
  guidance: record.guidance || '',
  fullParticipationFee: record.seminarFullParticipationFee ?? '',
  primary_image_id: record.primary_image_id || record.primary_image?.id || '',
  image: '',
  seminars: fallbackDefaults.seminars,
  slots: (record.slots || []).length
    ? record.slots.map(seminarSlotToDuplicateDefaults)
    : fallbackDefaults.slots,
});

const seriesToDuplicateDefaults = (record, fallbackDefaults = {}) => ({
  ...fallbackDefaults,
  title: '',
  subtitle: '',
  seriesKind: 'grouped',
  seriesTitle: `${record.title || ''} copy`,
  seriesSubtitle: record.subtitle || '',
  seriesSummary: record.summary || '',
  seriesOrganizerId: record.organizerId || '',
  seriesFullParticipationFee: record.fullParticipationFee ?? '',
  series_primary_image_id: record.primary_image_id || record.primary_image?.id || '',
  seriesImage: '',
  eventType: 'spot',
  organizerId: record.organizerId || '',
  isPublished: false,
  isActive: true,
  summary: '',
  guidance: '',
  fullParticipationFee: '',
  primary_image_id: '',
  image: '',
  seminars: (record.seminars || []).length
    ? record.seminars.map(seminarToGroupedDuplicateDefaults)
    : fallbackDefaults.seminars,
  slots: fallbackDefaults.slots,
});

const fetchSeminarDetailsForSeriesDuplicate = async (record = {}) => {
  const summaries = Array.isArray(record.seminars) ? record.seminars : [];
  const details = await Promise.all(
    summaries.map(async (seminar) => {
      if (!seminar?.id) {
        return seminar;
      }

      try {
        return await fetchYowanAdminSeminarDetail(seminar.id);
      } catch (error) {
        return seminar;
      }
    }),
  );

  return {
    ...record,
    seminars: details,
  };
};

const AdminEventCreatePage = () => {
  const searchParams = new URLSearchParams(useLocation().search);
  const isClassCreate = searchParams.get('type') === 'regular';
  const existingSeriesId = searchParams.get('seriesId') || '';
  const content = yowanPageContent[isClassCreate ? '/admin/events/classes/new' : '/admin/events/new'];
  const duplicateFrom = searchParams.get('duplicateFrom');
  const duplicateSeriesFrom = searchParams.get('duplicateSeriesFrom');
  const preservedDomainSearch = usePreservedDomainSearch();
  const navigate = useNavigate();
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [seriesPreset, setSeriesPreset] = useState(null);
  const [masterOptions, setMasterOptions] = useState(() => buildInitialMasterOptions(content?.form?.options || {}));
  const [slotIndexes, setSlotIndexes] = useState(
    Array.from({ length: content?.form.defaults?.slots?.length || content?.form.defaults?.schedules?.length || 0 }, (_, index) => index),
  );
  const initialSeminarIndexes = Array.from({ length: content?.form.defaults?.seminars?.length || 2 }, (_, index) => index);
  const [seminarIndexes, setSeminarIndexes] = useState(initialSeminarIndexes);
  const [seminarSlotIndexes, setSeminarSlotIndexes] = useState(
    Object.fromEntries(initialSeminarIndexes.map((index) => [index, [0]])),
  );
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: content?.form.defaults,
    shouldUnregister: true,
  });

  useEffect(() => {
    if (!content) {
      return undefined;
    }

    let isMounted = true;

    const loadMasters = async () => {
      setIsLoadingOptions(true);
      setLoadError('');

      try {
        const [
          organizers,
          instructors,
          facilities,
          rooms,
        ] = await Promise.all([
          fetchYowanAdminOrganizerList({ pageSize: ADMIN_EVENT_OPTION_PAGE_SIZE, isActive: true }),
          fetchYowanAdminInstructorList({ pageSize: ADMIN_EVENT_OPTION_PAGE_SIZE, isActive: true }),
          fetchYowanAdminFacilityList({ pageSize: ADMIN_EVENT_OPTION_PAGE_SIZE, isActive: true }),
          fetchYowanAdminRoomList({ pageSize: ADMIN_EVENT_OPTION_PAGE_SIZE, isActive: true }),
        ]);

        if (!isMounted) {
          return;
        }

        setMasterOptions((previousOptions) => ({
          ...content.form.options,
          organizers: buildOrganizerOptions(mergeRowsById(organizers.results, optionRowsToMasterRows(previousOptions.organizers, 'organizerName'))),
          instructors: buildInstructorOptions(mergeRowsById(instructors.results, optionRowsToMasterRows(previousOptions.instructors, 'instructorName'))),
          facilities: buildFacilityOptions(mergeRowsById(facilities.results, optionRowsToMasterRows(previousOptions.facilities, 'facilityName'))),
          rooms: buildRoomOptions(mergeRowsById(rooms.results, optionRowsToMasterRows(previousOptions.rooms, 'roomName'))),
        }));
      } catch (error) {
        if (isMounted) {
          setLoadError('選択肢を読み込めませんでした。時間をおいて再度お試しください。');
        }
      } finally {
        if (isMounted) {
          setIsLoadingOptions(false);
        }
      }
    };

    loadMasters();

    return () => {
      isMounted = false;
    };
  }, [content]);

  useEffect(() => {
    if (!content || isClassCreate || !existingSeriesId) {
      return undefined;
    }

    let isMounted = true;

    fetchYowanAdminSeminarSeriesDetail(existingSeriesId)
      .then((series) => {
        if (!isMounted) {
          return;
        }

        setSeriesPreset(series);
        setValue('seriesId', series.id);
        setValue('seriesKind', 'grouped');
        setValue('seriesTitle', series.title || '');
        setValue('seriesSubtitle', series.subtitle || '');
        setValue('seriesSummary', series.summary || '');
        setValue('seriesOrganizerId', series.organizerId || '');
        setValue('seriesFullParticipationFee', series.fullParticipationFee ?? '');
        setValue('series_primary_image_id', series.primary_image_id || series.primary_image?.id || '');
        if (series.organizerId) {
          setValue('organizerId', series.organizerId);
          setMasterOptions((previousOptions) => ({
            ...previousOptions,
            organizers: appendOptionIfMissing(
              previousOptions.organizers || [],
              buildMasterOption(
                { ...(series.organizer || {}), id: series.organizerId, retained: true },
                series.organizerName || series.organizer?.organizerName || series.organizerId,
                { retained: true },
              ),
            ),
          }));
        }
        setSubmitMessage(`${series.title || 'シリーズ'} にセミナーを追加します。`);
      })
      .catch(() => {
        if (isMounted) {
          setLoadError('追加先のシリーズを読み込めませんでした。');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [content, existingSeriesId, isClassCreate, setValue]);

  useEffect(() => {
    if (!content || isClassCreate || !duplicateFrom || duplicateSeriesFrom) {
      return undefined;
    }

    let isMounted = true;

    fetchYowanAdminSeminarDetail(duplicateFrom)
      .then((record) => {
        if (!isMounted) {
          return;
        }

        const defaults = seminarToDuplicateDefaults(record, content.form.defaults);

        setMasterOptions((previousOptions) => mergeReferencedMasterOptions(previousOptions, record));
        reset(defaults);
        setSlotIndexes(Array.from({ length: defaults.slots?.length || 1 }, (_, index) => index));
        setSeminarIndexes(Array.from({ length: defaults.seminars?.length || content.form.defaults?.seminars?.length || 2 }, (_, index) => index));
        setSeminarSlotIndexes(
          Object.fromEntries(
            Array.from({ length: defaults.seminars?.length || content.form.defaults?.seminars?.length || 2 }, (_, index) => [
              index,
              [0],
            ]),
          ),
        );
        setSubmitMessage(`${record.title} を複製して新規登録します。日時と受付状態は安全のため未設定/停止側に戻しています。`);
      })
      .catch(() => {
        if (isMounted) {
          setSubmitMessage('複製元を読み込めませんでした。通常の新規登録として作成してください。');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [content, duplicateFrom, duplicateSeriesFrom, isClassCreate, reset]);

  useEffect(() => {
    if (!content || isClassCreate || !duplicateSeriesFrom) {
      return undefined;
    }

    let isMounted = true;

    fetchYowanAdminSeminarSeriesDetail(duplicateSeriesFrom)
      .then(fetchSeminarDetailsForSeriesDuplicate)
      .then((record) => {
        if (!isMounted) {
          return;
        }

        const defaults = seriesToDuplicateDefaults(record, content.form.defaults);
        const nextSeminarIndexes = Array.from({ length: defaults.seminars?.length || 1 }, (_, index) => index);

        setMasterOptions((previousOptions) => mergeReferencedMasterOptions(previousOptions, record));
        reset(defaults);
        setSlotIndexes(Array.from({ length: defaults.slots?.length || 1 }, (_, index) => index));
        setSeminarIndexes(nextSeminarIndexes);
        setSeminarSlotIndexes(
          Object.fromEntries(
            nextSeminarIndexes.map((seminarIndex) => [
              seminarIndex,
              Array.from({ length: defaults.seminars?.[seminarIndex]?.slots?.length || 1 }, (_, index) => index),
            ]),
          ),
        );
        setSubmitMessage(`${record.title || 'シリーズ'} を複製して新規登録します。日時と受付状態は安全のため未設定/停止側に戻しています。`);
      })
      .catch(() => {
        if (isMounted) {
          setSubmitMessage('複製元のシリーズを読み込めませんでした。通常の新規登録として作成してください。');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [content, duplicateSeriesFrom, isClassCreate, reset]);

  if (!content) {
    return null;
  }

  const formContent = buildOptionsContent(content, masterOptions);
  const seriesKind = watch('seriesKind') || 'standalone';
  const isAddingToExistingSeries = !isClassCreate && Boolean(existingSeriesId);
  const isGroupedSeminarCreate = !isClassCreate && seriesKind === 'grouped' && !isAddingToExistingSeries;

  const onSubmit = async (values) => {
    const invalidDateTimeRangeMessage = content.form.errors.invalidDateTimeRange ||
      '終了日時は開始日時以降に設定してください。';

    if (!validateYowanAdminEventDateTimeRanges({
      values,
      setError,
      clearErrors,
      message: invalidDateTimeRangeMessage,
    })) {
      setSubmitMessage(invalidDateTimeRangeMessage);
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      if (isClassCreate) {
        await createYowanAdminClass(values);
        navigate(appendYowanPreviewQueryIfNeeded('/admin/events/classes', preservedDomainSearch));
      } else if (isAddingToExistingSeries) {
        const seminarValues = { ...values };
        delete seminarValues.publicSlug;
        await createYowanAdminSeminar({
          ...seminarValues,
          seriesId: existingSeriesId,
          seriesKind: 'grouped',
        });
        navigate(appendYowanPreviewQueryIfNeeded(`/admin/events/series/${existingSeriesId}`, preservedDomainSearch));
      } else if (values.seriesKind === 'grouped' && Array.isArray(values.seminars) && values.seminars.filter((seminar) => seminar?.title || seminar?.guidance).length >= 2) {
        await createYowanAdminSeminarSeries(values);
        navigate(appendYowanPreviewQueryIfNeeded('/admin/events/seminars', preservedDomainSearch));
      } else if (values.seriesKind === 'grouped' && Array.isArray(values.seminars) && values.seminars.length) {
        await createYowanAdminSeminar({
          ...values.seminars[0],
          publicSlug: values.publicSlug,
          seriesKind: 'standalone',
        });
        navigate(appendYowanPreviewQueryIfNeeded('/admin/events/seminars', preservedDomainSearch));
      } else {
        await createYowanAdminSeminar(values);
        navigate(appendYowanPreviewQueryIfNeeded('/admin/events/seminars', preservedDomainSearch));
      }
    } catch (error) {
      const publicSlugMessage = getYowanApiFieldErrorMessage(error, ['public_slug', 'series_public_slug', 'publicSlug']);

      if (publicSlugMessage) {
        setError('publicSlug', { type: 'server', message: publicSlugMessage });
        setSubmitMessage('公開URL slugを確認してください。');
      } else {
        setSubmitMessage(getYowanAdminMutationErrorMessage(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleAddSlot = () => {
    const nextIndex = slotIndexes.length ? Math.max(...slotIndexes) + 1 : 0;
    const nextPosition = slotIndexes.length;

    if (isClassCreate) {
      setValue(`schedules.${nextPosition}.scheduleKind`, 'weekly');
      setValue(`schedules.${nextPosition}.weekday`, 'tuesday');
      setValue(`schedules.${nextPosition}.locationIsPublic`, 'true');
      setValue(`schedules.${nextPosition}.isActive`, true);
    } else {
      setValue(`slots.${nextPosition}.title`, '');
      setValue(`slots.${nextPosition}.locationDisplayMode`, 'facility');
      setValue(`slots.${nextPosition}.showInstructorProfile`, false);
      setValue(`slots.${nextPosition}.isOpen`, true);
      setValue(`slots.${nextPosition}.isActive`, true);
    }

    setSlotIndexes([...slotIndexes, nextIndex]);
  };
  const handleRemoveSlot = (slotIndex) => {
    setSlotIndexes((currentIndexes) => {
      if (currentIndexes.length <= 1) {
        return currentIndexes;
      }

      return currentIndexes.filter((currentIndex) => currentIndex !== slotIndex);
    });
  };
  const handleAddSeminar = () => {
    const nextIndex = seminarIndexes.length ? Math.max(...seminarIndexes) + 1 : 0;
    const nextPosition = seminarIndexes.length;
    const defaultSeminar = createDefaultGroupedSeminar();

    setValue(`seminars.${nextPosition}`, defaultSeminar);
    setSeminarIndexes([...seminarIndexes, nextIndex]);
    setSeminarSlotIndexes((current) => ({
      ...current,
      [nextIndex]: [0],
    }));
  };
  const handleRemoveSeminar = (seminarIndex) => {
    setSeminarIndexes((currentIndexes) => {
      if (currentIndexes.length <= 2) {
        return currentIndexes;
      }

      return currentIndexes.filter((currentIndex) => currentIndex !== seminarIndex);
    });
  };
  const handleAddSeminarSlot = (seminarIndex) => {
    const currentSlotIndexes = seminarSlotIndexes[seminarIndex] || [0];
    const nextSlotIndex = currentSlotIndexes.length ? Math.max(...currentSlotIndexes) + 1 : 0;

    setValue(`seminars.${seminarIndex}.slots.${nextSlotIndex}`, createDefaultSeminarSlot());
    setSeminarSlotIndexes((current) => ({
      ...current,
      [seminarIndex]: [...currentSlotIndexes, nextSlotIndex],
    }));
  };
  const handleRemoveSeminarSlot = (seminarIndex, slotIndex) => {
    setSeminarSlotIndexes((current) => {
      const currentSlotIndexes = current[seminarIndex] || [0];

      if (currentSlotIndexes.length <= 1) {
        return current;
      }

      return {
        ...current,
        [seminarIndex]: currentSlotIndexes.filter((currentIndex) => currentIndex !== slotIndex),
      };
    });
  };

  return (
    <AdminBlogShell
      activeNavHref={isClassCreate ? '/admin/events/classes' : '/admin/events/seminars'}
      routeKey="/admin/events/new"
    >
      <AdminPageHeader
        title={content.header.title}
        paragraphs={
          isAddingToExistingSeries && seriesPreset
            ? [
                `${seriesPreset.title || 'シリーズ'} に新しいセミナーを追加します。シリーズ情報はここでは変更しません。`,
                ...content.header.paragraphs,
              ]
            : duplicateFrom && !isClassCreate
            ? [
                '既存セミナーを複製して新規登録します。日時と受付状態は安全のため未設定/停止側に戻します。',
                ...content.header.paragraphs,
              ]
            : duplicateSeriesFrom && !isClassCreate
            ? [
                '既存シリーズを複製して新規登録します。配下セミナーと開催コマの構成を引き継ぎ、日時と受付状態は安全側に戻します。',
                ...content.header.paragraphs,
              ]
            : content.header.paragraphs
        }
        actions={[
          <Link
            key="back-to-list"
            to={appendYowanPreviewQueryIfNeeded(isClassCreate ? '/admin/events/classes' : '/admin/events/seminars', preservedDomainSearch)}
          >
            一覧へ戻る
          </Link>,
        ]}
      />

      <form className="yowan-admin-form yowan-admin-event-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        {isLoadingOptions ? <LoadingSpinner size="sm" /> : null}
        {loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}
        {!isClassCreate ? (
          <SeminarSeriesFields
            content={formContent}
            register={register}
            watch={watch}
            errors={errors}
            readOnly={isAddingToExistingSeries}
          />
        ) : null}
        {isGroupedSeminarCreate ? (
          <AdminGroupedSeminarFields
            content={formContent}
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
            seminarIndexes={seminarIndexes}
            seminarSlotIndexes={seminarSlotIndexes}
            onAddSeminar={handleAddSeminar}
            onRemoveSeminar={handleRemoveSeminar}
            onAddSeminarSlot={handleAddSeminarSlot}
            onRemoveSeminarSlot={handleRemoveSeminarSlot}
          />
        ) : (
          <AdminEventEditorFields
            content={formContent}
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
            slotIndexes={slotIndexes}
            onAddSlot={handleAddSlot}
            onRemoveSlot={handleRemoveSlot}
            hidePublicSlug={!isClassCreate}
            renderAfterPublished={() => (!isClassCreate && formContent.form.labels.acceptsApplications ? (
              <AdminBooleanToggleField
                label={formContent.form.labels.acceptsApplications}
                name="acceptsApplications"
                register={register}
                helpText={formContent.form.acceptsApplicationsHelpText}
              />
            ) : null)}
          />
        )}

        <div className="yowan-admin-form__actions">
          <button
            type="submit"
            className="c-form__submit"
            disabled={isSubmitting || isLoadingOptions || Boolean(loadError) || (isAddingToExistingSeries && !seriesPreset)}
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" />
                <span>{content.form.pendingMessage}</span>
              </>
            ) : content.form.submitLabel}
          </button>
        </div>

        {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
      </form>
    </AdminBlogShell>
  );
};

export default AdminEventCreatePage;
